'use client';

import {useState, useEffect} from 'react';
import {useLocale} from 'next-intl';
import Card from '@/components/ui/Card';

interface Sondage {
  id: number;
  titre_fr: string;
  titre_ar?: string;
  description_fr?: string;
  questions_json: Array<{type: string; label_fr: string; label_ar?: string; options?: string[]}>;
  nb_reponses: number;
  date_fin?: string;
}

export default function SondagesPage() {
  const locale = useLocale();
  const [sondages, setSondages] = useState<Sondage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Sondage | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch('/api/v1/sondages/?publie_only=true')
      .then(r => r.ok ? r.json() : [])
      .then(setSondages)
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async () => {
    if (!selected) return;
    const res = await fetch(`/api/v1/sondages/${selected.id}/repondre`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({reponses_json: answers}),
    });
    if (res.ok) {
      setSubmitted(true);
      setSelected(null);
      setAnswers({});
    }
  };

  const title = locale === 'ar' ? 'استطلاعات الرأي' : locale === 'en' ? 'Surveys' : 'Sondages';
  const subtitle = locale === 'ar' ? 'شاركونا رأيكم لتحسين خدماتنا' : locale === 'en' ? 'Share your opinion to improve our services' : 'Partagez votre avis pour ameliorer nos services';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-primary mb-3">{title}</h1>
        <p className="text-gray-600 text-lg">{subtitle}</p>
      </div>

      {submitted && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 text-center text-green-800">
          {locale === 'ar' ? 'شكرا على مشاركتكم!' : locale === 'en' ? 'Thank you for your participation!' : 'Merci pour votre participation !'}
        </div>
      )}

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      ) : selected ? (
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-2">{locale === 'ar' && selected.titre_ar ? selected.titre_ar : selected.titre_fr}</h2>
          {selected.description_fr && <p className="text-gray-600 mb-6">{selected.description_fr}</p>}
          <div className="space-y-6">
            {selected.questions_json.map((q, idx) => (
              <div key={idx} className="border-b pb-4">
                <label className="block font-semibold mb-2">
                  {idx + 1}. {locale === 'ar' && q.label_ar ? q.label_ar : q.label_fr}
                </label>
                {q.type === 'texte' ? (
                  <textarea
                    className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-primary focus:outline-none"
                    rows={3}
                    value={answers[String(idx)] || ''}
                    onChange={e => setAnswers({...answers, [String(idx)]: e.target.value})}
                  />
                ) : q.type === 'echelle' ? (
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(v => (
                      <button
                        key={v}
                        className={`w-10 h-10 rounded-full border-2 font-bold ${answers[String(idx)] === String(v) ? 'bg-primary text-white border-primary' : 'border-gray-300 hover:border-primary'}`}
                        onClick={() => setAnswers({...answers, [String(idx)]: String(v)})}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                ) : q.options ? (
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => (
                      <label key={oi} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type={q.type === 'choix_multiple' ? 'checkbox' : 'radio'}
                          name={`q_${idx}`}
                          value={opt}
                          checked={answers[String(idx)] === opt}
                          onChange={() => setAnswers({...answers, [String(idx)]: opt})}
                          className="w-4 h-4 text-primary focus:ring-primary"
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-6">
            <button
              onClick={() => { setSelected(null); setAnswers({}); }}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {locale === 'ar' ? 'رجوع' : locale === 'en' ? 'Back' : 'Retour'}
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              {locale === 'ar' ? 'إرسال' : locale === 'en' ? 'Submit' : 'Envoyer'}
            </button>
          </div>
        </Card>
      ) : sondages.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          {locale === 'ar' ? 'لا توجد استطلاعات حاليا' : locale === 'en' ? 'No surveys available' : 'Aucun sondage disponible pour le moment'}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {sondages.map(s => (
            <Card key={s.id} className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setSelected(s)}>
              <h3 className="text-lg font-bold mb-2">{locale === 'ar' && s.titre_ar ? s.titre_ar : s.titre_fr}</h3>
              {s.description_fr && <p className="text-gray-600 text-sm mb-3">{s.description_fr}</p>}
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{s.questions_json.length} {locale === 'ar' ? 'أسئلة' : locale === 'en' ? 'questions' : 'questions'}</span>
                <span>{s.nb_reponses} {locale === 'ar' ? 'مشارك' : locale === 'en' ? 'responses' : 'reponses'}</span>
              </div>
              {s.date_fin && (
                <p className="text-xs text-gray-400 mt-2">
                  {locale === 'ar' ? 'ينتهي في' : locale === 'en' ? 'Ends' : 'Termine le'} {new Date(s.date_fin).toLocaleDateString(locale)}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
